import { useEffect, useState } from "react";
import { useDataProvider, useGetList, useUpdate } from "ra-core";
import { Link } from "react-router";
import { Bell } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";

import { STUDIO_EMAIL } from "../../../../supabase/functions/_shared/vogelkop.ts";
import type { Alert } from "../types";
import { scanDueReminders } from "./alerts";

export const AlertBell = () => {
  const dataProvider = useDataProvider();
  const [update] = useUpdate();
  const { data: alerts = [], refetch } = useGetList<Alert>("alerts", {
    pagination: { page: 1, perPage: 20 },
    sort: { field: "created_at", order: "DESC" },
  });
  const [open, setOpen] = useState(false);

  useEffect(() => {
    let cancelled = false;
    void scanDueReminders(dataProvider).then(() => {
      if (!cancelled) void refetch();
    });
    return () => {
      cancelled = true;
    };
  }, [dataProvider, refetch]);

  const unread = alerts.filter((alert) => !alert.read_at);

  const markRead = (alert: Alert) => {
    if (alert.read_at) return;
    update("alerts", {
      id: alert.id,
      data: { read_at: new Date().toISOString() },
      previousData: alert,
    });
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="ghost"
          size="icon"
          className="relative"
          aria-label={
            unread.length ? `Alerts, ${unread.length} unread` : "Alerts"
          }
        >
          <Bell className="size-5" />
          {unread.length > 0 ? (
            <span className="absolute -top-0.5 -right-0.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-rose-600 px-1 text-[10px] font-semibold text-white">
              {unread.length}
            </span>
          ) : null}
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-96 p-0">
        <div className="border-b px-4 py-3">
          <h2 className="font-semibold">Alerts</h2>
          <p className="text-xs text-muted-foreground">
            New leads and reminders due today. Emailed to {STUDIO_EMAIL}.
          </p>
        </div>
        <ul className="max-h-96 overflow-auto">
          {alerts.length === 0 ? (
            <li className="px-4 py-6 text-sm text-muted-foreground">
              No alerts.
            </li>
          ) : (
            alerts.map((alert) => (
              <li key={alert.id} className="border-b last:border-b-0">
                <Link
                  to={`/contacts/${alert.contact_id}/show`}
                  className="block px-4 py-3 no-underline hover:bg-muted"
                  onClick={() => {
                    markRead(alert);
                    setOpen(false);
                  }}
                >
                  <p
                    className={
                      alert.read_at ? "text-sm" : "text-sm font-semibold"
                    }
                  >
                    {alert.title}
                  </p>
                  <p className="text-sm text-muted-foreground">{alert.body}</p>
                  <p className="mt-1 text-xs text-muted-foreground">
                    {alert.kind === "new_lead" ? "New lead" : "Reminder due"}
                    {alert.emailed_at ? ` · Emailed ${STUDIO_EMAIL}` : ""}
                  </p>
                </Link>
              </li>
            ))
          )}
        </ul>
      </PopoverContent>
    </Popover>
  );
};
